import React, { useEffect, useState } from "react";

interface Avatar {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

interface ExpiringLink {
  id: string;
  slug: string;
  expiresAt: string;
  avatarId: string;
  timezone: string;
  displayName: string;
  createdAt: string;
  updatedAt: string;
  expired: boolean;
  fullUrl: string;
  avatar?: {
    id: string;
    name: string;
  };
}

interface DashboardProps {
  app_name: string;
  expiringLinks: ExpiringLink[];
}

const Dashboard: React.FC<DashboardProps> = (props) => {
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [expiringLinks, setExpiringLinks] = useState<ExpiringLink[]>(
    props.expiringLinks || []
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAvatars();
  }, []);

  const fetchAvatars = async () => {
    try {
      const response = await fetch("/api/v1/avatars");
      const data = await response.json();
      setAvatars(data);
    } catch (error) {
      console.error("Error fetching avatars:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this avatar?")) return;

    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");
      const response = await fetch(`/api/v1/avatars/${id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrfToken || "",
        },
      });

      if (response.ok) {
        setAvatars(avatars.filter((avatar) => avatar.id !== id));
      }
    } catch (error) {
      console.error("Error deleting avatar:", error);
    }
  };

  const handleDeleteLink = async (id: string) => {
    if (!confirm("Are you sure you want to delete this expiring link?")) return;

    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");
      const response = await fetch(`/api/v1/expiring_links/${id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrfToken || "",
        },
      });

      if (response.ok) {
        setExpiringLinks(expiringLinks.filter((link) => link.id !== id));
      }
    } catch (error) {
      console.error("Error deleting expiring link:", error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </div>
    );
  }

  return (
    <React.StrictMode>
      <div className="p-6 space-y-12">
        {/* Avatars Section */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-4xl font-bold">Avatars</h1>
            <a href="/avatars/new" className="btn btn-primary">
              New Avatar
            </a>
          </div>

          {avatars.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-gray-500 mb-4">No avatars yet</p>
              <a href="/avatars/new" className="btn btn-primary">
                Create Your First Avatar
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {avatars.map((avatar) => (
                <div key={avatar.id} className="card bg-base-200 shadow-xl">
                  <div className="card-body">
                    <h2 className="card-title">{avatar.name}</h2>
                    <div className="card-actions justify-end mt-4">
                      <a
                        href={`/avatars/${avatar.id}`}
                        className="btn btn-sm btn-primary"
                      >
                        View
                      </a>
                      <a
                        href={`/avatars/${avatar.id}/edit`}
                        className="btn btn-sm btn-ghost"
                      >
                        Edit
                      </a>
                      <button
                        onClick={() => handleDelete(avatar.id)}
                        className="btn btn-sm btn-error"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Expiring Links Section */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-bold">Expiring Links</h2>
            <a href="/expiring_links/new" className="btn btn-primary">
              New Expiring Link
            </a>
          </div>

          {expiringLinks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-gray-500 mb-4">
                No expiring links yet
              </p>
              <a href="/expiring_links/new" className="btn btn-primary">
                Create Your First Expiring Link
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {expiringLinks.map((link) => (
                <div
                  key={link.id}
                  className={`card shadow-xl ${
                    link.expired ? "bg-base-300" : "bg-base-200"
                  }`}
                >
                  <div className="card-body">
                    <div className="flex items-start justify-between">
                      <h2 className="card-title">{link.slug}</h2>
                      {link.expired ? (
                        <span className="badge badge-error">Expired</span>
                      ) : (
                        <span className="badge badge-success">Active</span>
                      )}
                    </div>
                    <div className="text-sm space-y-1 mt-2">
                      <p className="text-gray-600">
                        <strong>Avatar:</strong> {link.avatar?.name || "N/A"}
                      </p>
                      <p className="text-gray-600">
                        <strong>Expires:</strong> {formatDate(link.expiresAt)}
                      </p>
                      <p className="text-gray-600 truncate">
                        <strong>URL:</strong>{" "}
                        <a
                          href={link.fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link link-primary"
                        >
                          {link.fullUrl}
                        </a>
                      </p>
                    </div>
                    <div className="card-actions justify-end mt-4">
                      <a
                        href={`/expiring_links/${link.id}`}
                        className="btn btn-sm btn-primary"
                      >
                        View
                      </a>
                      <a
                        href={`/expiring_links/${link.id}/edit`}
                        className="btn btn-sm btn-ghost"
                      >
                        Edit
                      </a>
                      <button
                        onClick={() => handleDeleteLink(link.id)}
                        className="btn btn-sm btn-error"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </React.StrictMode>
  );
};

export default Dashboard;
